import React, { useState } from "react";
import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { UserData } from "models/interfaces";
import SignaturePad from "screens/SignRequest/ParticipantScreen/SignaturePad/SignaturePad";
import {
  uploadSelfSignInitialsImage,
  uploadSelfSignSignImage,
} from "screens/SignRequest/SignRequestService";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { updateSignature } from "redux/authSlice";
import { selectScreen } from "redux/appSlice";
import { fetchProfileDetails } from "screens/Settings/SettingsService";
import { getProfileFormData } from "screens/Settings/General/GeneralTypesAndValidation";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import myProfileStyles from "screens/Settings/MyProfile/MyProfile.styles";
import profileTokens, {
  profileResponsiveTokens,
} from "screens/Settings/MyProfile/MyProfile.tokens";
import strings from "global/constants/StringConstants";
import { SignatureEditIcon } from "./ProfileIcons";

const styles = myProfileStyles;
const { signature } = profileTokens;
const tabletSignature = profileResponsiveTokens.tablet.signature;

interface SignaturesAndInitialsCardProps {
  profile: UserData;
  setProfile: React.Dispatch<React.SetStateAction<UserData>>;
}

interface PreviewTileProps {
  label: string;
  width: number;
  /** The 834 reference runs both tiles narrower than the desktop one. */
  tabletWidth: number;
  image?: string;
  emptyLabel: string;
  onEdit: () => void;
}

/**
 * One "SIGNATURE" / "INITIALS" slot: a labelled cream tile that either shows
 * the saved mark above a ruled baseline (with a round edit affordance) or a
 * dashed "add" target when nothing has been saved yet.
 */
const PreviewTile: React.FC<PreviewTileProps> = ({
  label,
  width,
  tabletWidth,
  image,
  emptyLabel,
  onEdit,
}) => {
  const resolvedImage = image
    ? image.startsWith("data:") || image.startsWith("http")
      ? image
      : `data:image/png;base64,${image}`
    : null;

  return (
    <Box sx={styles.signatureColumn(width, tabletWidth)}>
      <Typography component="div" sx={styles.signatureLabel}>
        {label}
      </Typography>

      {resolvedImage ? (
        // The whole tile is a single edit control, so the mouse target and the
        // keyboard target are one and the same (no redundant nested button).
        <ButtonBase
          disableRipple
          onClick={onEdit}
          aria-label={`Edit ${label.toLowerCase()}`}
          sx={styles.signatureTile}
        >
          <Box
            component="img"
            src={resolvedImage}
            alt=""
            sx={{
              maxHeight: signature.markMaxHeight,
              maxWidth: signature.markMaxWidth,
              objectFit: "contain",
            }}
          />
          <Box sx={styles.signatureBaseline} />
          <Box
            component="span"
            aria-hidden="true"
            sx={styles.signatureEditButton}
          >
            <SignatureEditIcon sx={styles.signatureEditIcon} />
          </Box>
        </ButtonBase>
      ) : (
        <ButtonBase
          disableRipple
          onClick={onEdit}
          aria-label={emptyLabel}
          sx={styles.signatureTileEmpty}
        >
          <Box component="span" sx={styles.signatureEmptyPlus}>
            +
          </Box>
          <Box component="span" sx={styles.signatureEmptyLabel}>
            {emptyLabel}
          </Box>
        </ButtonBase>
      )}
    </Box>
  );
};

export const SignaturesAndInitialsSkeleton = () => (
  <Box sx={styles.formCard}>
    <Box sx={styles.cardHeader}>
      <Skeleton variant="text" width={170} height={20} />
      <Skeleton variant="text" width="55%" height={16} />
    </Box>
    <Box sx={styles.signatureRow}>
      {[
        [signature.signatureWidth, tabletSignature.signatureWidth],
        [signature.initialsWidth, tabletSignature.initialsWidth],
      ].map(([width, tabletWidth]) => (
        <Box key={width} sx={styles.signatureColumn(width, tabletWidth)}>
          <Skeleton variant="text" width={80} height={14} />
          <Skeleton
            variant="rounded"
            width="100%"
            height={signature.tileHeight}
            sx={{ borderRadius: signature.tileRadius }}
          />
        </Box>
      ))}
    </Box>
  </Box>
);

const SignaturesAndInitialsCard: React.FC<SignaturesAndInitialsCardProps> = ({
  profile,
  setProfile,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useAppSelector(selectScreen);

  const [uploading, setUploading] = useState<boolean>(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState<boolean>(false);
  const [initialsModalOpen, setInitialsModalOpen] = useState<boolean>(false);
  const [signatureField, setSignatureField] = useState<any>({
    name: "signature",
    value: "",
  });
  const [initialsField, setInitialsField] = useState<any>({
    name: "initials",
    value: "",
  });

  const base64ToImageFile = (
    base64String: string,
    fileName: string,
  ): Promise<File | null> => {
    const prefix = base64String.split(",")[0];
    const match = prefix.match(/^data:(.*?);base64$/);
    const contentType = match ? match[1] : "";
    const byteCharacters = atob(base64String.split(",")[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = 270;
        canvas.height = 130;
        context?.drawImage(img, 0, 0, 270, 130);
        canvas.toBlob((croppedBlob) => {
          if (croppedBlob) {
            const file = new File([croppedBlob], fileName, {
              type: contentType,
            });
            resolve(file);
          } else {
            resolve(null);
          }
        });
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  const handleImageUpload = async (
    bs64string: string,
    isInitialsImg: boolean,
  ) => {
    try {
      setUploading(true);
      const base64String: string = bs64string;
      /*
        Unique per upload, matching what the signing flow sends
        (`DraggableField`): a fixed `signature.png` / `initials.png` is the same
        key every time, so a replaced image can be served stale from cache.
      */
      const fileName = `${
        isInitialsImg ? strings.initials : strings.signature
      }${Date.now()}.png`;

      const file = await base64ToImageFile(base64String, fileName);
      let newformData = new FormData();
      newformData.append("file", file!, file!.name);

      if (isInitialsImg) {
        await uploadSelfSignInitialsImage(newformData);
      } else {
        await uploadSelfSignSignImage(newformData);
        dispatch(updateSignature(base64String));
      }

      const updatedProfile = await fetchProfileDetails();
      const parsedProfile = getProfileFormData(updatedProfile);

      setProfile((prev: any) => {
        const updated = { ...parsedProfile };
        if (isInitialsImg) {
          updated.initialsImage = base64String;
          updated.signatureImage =
            updated.signatureImage || prev.signatureImage;
        } else {
          updated.signatureImage = base64String;
          updated.initialsImage = updated.initialsImage || prev.initialsImage;
        }
        return updated;
      });

      openSuccessNotification(
        isInitialsImg
          ? "Initials updated successfully"
          : "Signature updated successfully",
      );
      if (isInitialsImg) {
        setInitialsModalOpen(false);
      } else {
        setSignatureModalOpen(false);
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {uploading ? (
        <SignaturesAndInitialsSkeleton />
      ) : (
        <Box sx={styles.formCard}>
          <Box sx={styles.cardHeader}>
            <Typography component="h2" sx={styles.cardTitle}>
              Signatures &amp; Initials
            </Typography>
            <Typography component="div" sx={styles.cardDescription}>
              How you appear on every signed document. Draw, type, or upload.
            </Typography>
          </Box>

          <Box sx={styles.signatureRow}>
            <PreviewTile
              label="SIGNATURE"
              width={signature.signatureWidth}
              tabletWidth={tabletSignature.signatureWidth}
              image={profile.signatureImage}
              emptyLabel="Add signature"
              onEdit={() => setSignatureModalOpen(true)}
            />
            <PreviewTile
              label="INITIALS"
              width={signature.initialsWidth}
              tabletWidth={tabletSignature.initialsWidth}
              image={(profile as any).initialsImage}
              emptyLabel="Add initials"
              onEdit={() => setInitialsModalOpen(true)}
            />
          </Box>
        </Box>
      )}

      <SignaturePad
        dispatch={() => {}}
        selectedFile={{} as any}
        field={signatureField}
        setField={setSignatureField}
        isOpen={signatureModalOpen}
        setIsOpen={setSignatureModalOpen}
        handleImageUpload={handleImageUpload}
        width={isMobile ? 310 : 500}
        height={isMobile ? 390 : 270}
        isInitialsPad={false}
        currentFieldOnFocus={0}
        moveToFieldLocation={() => {}}
      />
      <SignaturePad
        dispatch={() => {}}
        selectedFile={{} as any}
        field={initialsField}
        setField={setInitialsField}
        isOpen={initialsModalOpen}
        setIsOpen={setInitialsModalOpen}
        handleImageUpload={handleImageUpload}
        width={isMobile ? 310 : 500}
        height={isMobile ? 390 : 270}
        isInitialsPad={true}
        currentFieldOnFocus={0}
        moveToFieldLocation={() => {}}
      />
    </>
  );
};

export default SignaturesAndInitialsCard;
